import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
  Building2, CreditCard, MessageCircle, AlertCircle,
  TrendingUp, Activity, ArrowUpRight,
  Zap, Smartphone, Send, RefreshCw,
  Shield, Terminal, Award, Package,
  CheckCircle, FileText, ChevronRight
} from "lucide-react";
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../utils/cn';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import { PageLoader } from '../../components/crm/ui';

// Superadmin views aggregate figures across every tenant, so there's no
// single "company currency" to read here (unlike tenant-scoped CRM pages) —
// platform revenue is always billed/reported in the platform's own currency.
const PLATFORM_CURRENCY = 'INR';

const fmt      = (n) => (n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n ?? 0));
const fmtMoney = (n) => formatCurrency(n, PLATFORM_CURRENCY);

/* â”€â”€ Count-up hook â”€â”€ */
function useCountUp(target, duration = 1200, active = true) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!active || target === 0) { setValue(target); return; }
    const start = performance.now();
    const num = parseFloat(String(target).replace(/[^0-9.]/g, '')) || 0;
    const isFloat = String(target).includes('.');

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = num * ease;
      setValue(isFloat ? current : Math.floor(current));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };

    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, active]);

  return value;
}

/* â”€â”€ Animated KPI value display â”€â”€ */
function AnimatedValue({ raw, prefix = '', suffix = '', isReady }) {
  const num    = parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
  const isK    = String(raw).includes('k');
  const isM    = String(raw).includes('M');
  const counted = useCountUp(num, 1100, isReady);

  let display = Math.floor(counted).toLocaleString('en-IN');
  if (isK)      display = (counted).toFixed(1) + 'k';
  else if (isM) display = (counted).toFixed(1) + 'M';

  return <>{prefix}{display}{suffix}</>;
}

/* â”€â”€ Bar Chart component â”€â”€ */
function BarChart({ data, color = 'bg-[#0176D3]', height = 180 }) {
  const max = Math.max(...data.map(d => d.value), 1);
  const [hovered, setHovered] = useState(null);

  return (
    <div className="w-full relative group/chart pt-10" style={{ height }}>
      <div className="flex items-end justify-between h-[80%] gap-2 px-2">
        {data.map((d, i) => (
          <div
            key={i}
            className="flex-1 relative flex flex-col items-center group/bar"
            onMouseEnter={() => setHovered({ label: d.label, value: d.value })}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Tooltip */}
            <div className={cn(
 "absolute -top-14 px-4 py-2 rounded-lg bg-[#ffffff] border border-[#D8DDE6] text-[11px] font-bold text-white whitespace-nowrap shadow-sm transition-all duration-300 pointer-events-none z-10",
 hovered?.label === d.label ? 'opacity-100 -translate-y-2' : 'opacity-0 translate-y-0'
 )}>
               <span className="text-[#706E6B] mr-2 ">{d.label}:</span>
               {d.value.toLocaleString()}
            </div>
            
            <div 
               className={cn(
 "w-full max-w-[24px] rounded-t-lg transition-all duration-500 relative overflow-hidden shadow-sm hover:brightness-125",
 color
 )}
               style={{ height: `${Math.max((d.value / max) * 100, 4)}%` }}
            >
               <div className="absolute inset-0 bg-white " />
            </div>
          </div>
        ))}
      </div>
      
      {/* X-Axis */}
      <div className="flex justify-between items-center mt-6 px-2 border-t border-[#D8DDE6] pt-5">
         {data.map((d, i) => (
            <div key={i} className="flex-1 text-center text-[9px] font-bold text-[#706E6B] truncate">{d.label}</div>
         ))}
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [stats, setStats]       = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [isReady, setIsReady]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setIsReady(false);
      const [sRes, aRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/analytics'),
      ]);
      setStats(sRes.data.stats || sRes.data);
      setAnalytics(aRes.data.analytics || aRes.data);
      setTimeout(() => setIsReady(true), 120);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to synchronise platform statistics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <PageLoader />
  );

  if (error) return (
    <div className="w-full px-8 py-20">
      <Card className="p-12 flex flex-col items-center justify-center text-center gap-8 border-[#BA0517]/10">
         <div className="w-20 h-20 rounded bg-[#BA0517]/5 border border-[#BA0517]/10 flex items-center justify-center text-[#BA0517] shadow-sm">
            <AlertCircle size={48} strokeWidth={1} />
         </div>
         <div className="space-y-2">
            <h3 className="text-2xl font-bold text-[#080707] tracking-tight">Sync Failure</h3>
            <p className="text-[11px] font-bold text-[#706E6B] ">{error}</p>
         </div>
         <Button variant="primary" icon={RefreshCw} onClick={load} >Retry Sync</Button>
      </Card>
    </div>
  );

  const revenueData = (analytics?.revenueByMonth || analytics?.revenueChart || []).map(d => ({
    label: d.label || (d.month ? d.month.substring(0, 3) : ''),
    value: d.revenue || 0,
  }));

  const msgData = (analytics?.messagesByDay || analytics?.dailyMessages || []).map(d => ({
    label: d._id ? d._id.slice(8) : (d.day ? String(d.day).padStart(2, '0') : ''),
    value: d.count || d.messages || 0,
  }));

  const planDist    = analytics?.planDistribution || [];
  const maxPlanCount = Math.max(...planDist.map(p => p.count || 0), 1);
  const planColors  = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#F43F5E'];

  const activeRatio = stats?.totalCompanies ? Math.round((stats.activeCompanies / stats.totalCompanies) * 100) : 0;

  return (
    <div className="w-full pb-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
      
      <PageHeader
        title="System Overview"
        icon={Activity}
        subtitle="Real-time monitoring of platform infrastructure health and business metrics."
        actions={<>
          {stats?.thisMonthNewCompanies > 0 && (
            <Badge variant="emerald" className="h-8 px-3 text-[13px] font-semibold">
              <TrendingUp size={14} className="mr-2 inline" /> +{stats.thisMonthNewCompanies} New Accounts
            </Badge>
          )}
          <button onClick={load} className="h-8 px-3 bg-white border border-[#C9C9C9] text-[#0176D3] rounded whitespace-nowrap text-[13px] font-semibold hover:bg-[#F3F2F2] inline-flex items-center gap-2">
            <RefreshCw size={14} /> Refresh
          </button>
        </>}
      />

      {/* â”€â”€ Metric Matrix â”€â”€ */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Clients', val: stats?.totalCompanies, sub: `+${stats?.thisMonthNewCompanies || 0} this month`, icon: Building2, color: 'text-[#2E844A]', bg: 'bg-[#2E844A]/10' },
          { label: 'Active Accounts', val: stats?.activeCompanies, sub: `${activeRatio}% active rate`, icon: CheckCircle, color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
          { label: 'Total Revenue', val: stats?.totalRevenue, prefix: getCurrencySymbol(PLATFORM_CURRENCY), sub: `${fmtMoney(stats?.thisMonthRevenue)} monthly`, icon: CreditCard, color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
          { label: 'Current Revenue', val: stats?.thisMonthRevenue, prefix: getCurrencySymbol(PLATFORM_CURRENCY), sub: `${stats?.thisMonthPaidInvoices || 0} paid invoices`, icon: Award, color: 'text-[#DD7A01]', bg: 'bg-[#DD7A01]/10' },
          { label: 'Total Messages', val: stats?.totalMessages, sub: `${fmt(stats?.thisMonthMessages)} this month`, icon: Send, color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
          { label: 'Active Devices', val: stats?.connectedDevices ?? stats?.activeDevices, sub: `Of ${fmt(stats?.totalDevices)} total devices`, icon: Smartphone, color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
          { label: 'Pending Invoices', val: stats?.pendingInvoices, sub: `${fmtMoney(stats?.pendingRevenue)} outstanding`, icon: FileText, color: 'text-[#BA0517]', bg: 'bg-[#BA0517]/10' },
          { label: 'Subscription Plans', val: stats?.totalPlans, sub: "Active tiers", icon: Zap, color: 'text-[#0176D3]', bg: 'bg-[#0176D3]/10' },
        ].map((kpi, i) => (
          <Card key={i} className="p-6 group border-[#E5E5E5] bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
             <div className="space-y-4">
                 <div className="flex items-center justify-between">
                    <p className="text-[13px] font-semibold text-[#706E6B]">{kpi.label}</p>
                    <div className={cn(
 "w-8 h-8 rounded-md flex items-center justify-center",
 kpi.bg, kpi.color
 )}>
                       <kpi.icon size={16} strokeWidth={2} />
                    </div>
                 </div>
                 <div className="text-2xl font-bold text-[#080707] tabular-nums tracking-tight">
                    <AnimatedValue raw={kpi.val} prefix={kpi.prefix} isReady={isReady} />
                 </div>
                 <div className="flex items-center justify-between pt-2">
                    <span className="text-[12px] text-[#706E6B]">{kpi.sub}</span>
                 </div>
             </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        
        {/* Revenue Analytics */}
        <Card className="p-0 overflow-hidden bg-white border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="p-6 border-b border-[#D8DDE6] flex items-center justify-between">
               <div className="space-y-1">
                  <h3 className="text-[14px] font-bold text-[#2E2E2E] flex items-center gap-2">
                     <TrendingUp size={16} className="text-[#0176D3]" /> Revenue Analytics
                  </h3>
                  <p className="text-[12px] text-[#706E6B]">12-month billing settlement cycle</p>
               </div>
            </div>
           <div className="p-10">
              {revenueData.length > 0 ? (
                <BarChart data={revenueData} color="bg-[#2E844A]" height={220} />
              ) : (
                <div className="py-20 flex flex-col items-center justify-center opacity-20 gap-4 text-center">
                   <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#D8DDE6]" />
                   <p className="text-[10px] font-bold ">Waiting for Data…</p>
                </div>
              )}
           </div>
        </Card>

        {/* Messaging Activity */}
        <Card className="p-0 overflow-hidden bg-white border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="p-6 border-b border-[#D8DDE6] flex items-center justify-between">
               <div className="space-y-1">
                  <h3 className="text-[14px] font-bold text-[#2E2E2E] flex items-center gap-2">
                     <Activity size={16} className="text-[#0176D3]" /> Messaging Activity
                  </h3>
                  <p className="text-[12px] text-[#706E6B]">30-day broadcast volume metrics</p>
               </div>
            </div>
           <div className="p-10">
              {msgData.length > 0 ? (
                <BarChart data={msgData} color="bg-[#0176D3]" height={220} />
              ) : (
                <div className="py-20 flex flex-col items-center justify-center opacity-20 gap-4 text-center">
                   <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#D8DDE6]" />
                   <p className="text-[10px] font-bold ">No Traffic Detected</p>
                </div>
              )}
           </div>
        </Card>

        {/* Plan Distribution */}
        <Card className="p-0 overflow-hidden bg-white border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="p-6 border-b border-[#D8DDE6] flex items-center justify-between">
               <div className="space-y-1">
                  <h3 className="text-[14px] font-bold text-[#2E2E2E] flex items-center gap-2">
                     <Package size={16} className="text-[#0176D3]" /> Plan Distribution
                  </h3>
                  <p className="text-[12px] text-[#706E6B]">Client allocation per plan type</p>
               </div>
            </div>
           <div className="p-10 flex flex-col gap-6">
              {planDist.length > 0 ? (
                 planDist.map((p, i) => (
                    <div key={p._id || i} className="group space-y-3 p-4 rounded-lg border border-[#E5E5E5] hover:bg-[#F4F6F9] transition-all relative overflow-hidden bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                        <div className="flex justify-between items-center relative z-10">
                           <div className="space-y-1">
                              <div className="text-[14px] font-bold text-[#080707] truncate">{p.planName || p._id || 'Starter Plan'}</div>
                              <div className="text-[12px] text-[#706E6B]">{p.count} Accounts</div>
                           </div>
                           <div className="text-right">
                              <div className="text-[16px] font-bold text-[#080707] tabular-nums tracking-tight">{fmtMoney(p.revenue)}</div>
                              <div className="text-[11px] text-[#706E6B]">Revenue</div>
                           </div>
                        </div>
                       <div className="h-1.5 w-full bg-[#F3F2F2] rounded-full overflow-hidden">
                          <div 
                             className="h-full transition-all duration-1000" 
                             style={{ 
                                width: `${Math.round((p.count / maxPlanCount) * 100)}%`,
                                background: planColors[i % planColors.length],
                                boxShadow: `0 0 10px ${planColors[i % planColors.length]}40`
                             }} 
                          />
                       </div>
                    </div>
                 ))
              ) : (
                <div className="py-20 text-center opacity-30 text-[10px] font-bold ">No active distributions</div>
              )}
           </div>
        </Card>

        {/* Top Accounts */}
        <Card className="p-0 overflow-hidden bg-white border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <div className="p-6 border-b border-[#D8DDE6] flex items-center justify-between">
               <div className="space-y-1">
                  <h3 className="text-[14px] font-bold text-[#2E2E2E] flex items-center gap-2">
                     <Award size={16} className="text-[#0176D3]" /> Top Accounts
                  </h3>
                  <p className="text-[12px] text-[#706E6B]">Most active business accounts by volume</p>
               </div>
               <button onClick={() => window.location.href='/superadmin/companies'} className="text-[13px] font-bold text-[#0176D3] hover:underline flex items-center">View All <ChevronRight size={14} className="ml-1" /></button>
            </div>
           <div className="overflow-x-auto scrollbar-hide">
              <table className="w-full text-left border-collapse">
                 <thead>
                   <tr className="border-b border-[#D8DDE6] bg-[#F4F6F9] text-[12px] font-semibold text-[#706E6B]">
                     <th className="pl-6 py-3 font-semibold">Rank</th>
                     <th className="py-3 font-semibold">Company</th>
                     <th className="py-3 font-semibold">Plan</th>
                     <th className="pr-6 py-3 text-right font-semibold">Revenue</th>
                   </tr>
                 </thead>
                 <tbody>
                   {(analytics?.topCompanies || []).map((c, i) => (
                      <tr key={c._id || i} className="group hover:bg-[#F4F6F9] transition-colors border-b border-[#D8DDE6] last:border-0">
                         <td className="pl-6 py-3">
                            <span className="text-[13px] text-[#706E6B] font-bold">{i + 1}</span>
                         </td>
                         <td className="py-3">
                            <div className="text-[14px] font-bold text-[#0176D3] hover:underline cursor-pointer truncate max-w-[180px]">{c.companyName || c.name || 'Anonymous'}</div>
                            <div className="text-[12px] text-[#706E6B] mt-0.5">{c.email}</div>
                         </td>
                         <td className="py-3">
                            <Badge variant="blue" className="text-[11px] font-semibold">{c.planName || 'TRIAL'}</Badge>
                         </td>
                         <td className="pr-6 py-3 text-right text-[14px] font-bold text-[#080707] tabular-nums tracking-tight">
                            {fmtMoney(c.revenue)}
                         </td>
                      </tr>
                   ))}
                </tbody>
              </table>
              {(analytics?.topCompanies || []).length === 0 && (
                <div className="py-20 text-center opacity-20 text-[10px] font-bold ">No identity records</div>
              )}
           </div>
           
           <div className="p-4 bg-[#F4F6F9] border-t border-[#D8DDE6] flex items-center justify-between text-[11px] text-[#706E6B]">
               <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#2E844A]" /> Operational
               </div>
               <div className="tabular-nums font-mono">Server Time: {new Date().toLocaleTimeString()}</div>
            </div>
        </Card>

      </div>
    </div>
  );
}
